import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Check, X, AlertCircle, Info, Trash2, Edit3, Plus } from 'lucide-react';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastAction = 'create' | 'update' | 'delete' | 'submit' | 'save' | 'publish' | 'draft';

export interface Toast {
  id: string;
  type: ToastType;
  action?: ToastAction;
  title: string;
  message?: string;
  duration?: number;
  autoClose?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
  // Convenience methods for common actions
  showSuccess: (title: string, message?: string, action?: ToastAction) => void;
  showError: (title: string, message?: string, action?: ToastAction) => void;
  showWarning: (title: string, message?: string, action?: ToastAction) => void;
  showInfo: (title: string, message?: string, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const getActionIcon = (action?: ToastAction) => {
  switch (action) {
    case 'create':
    case 'submit':
      return <Plus size={16} />;
    case 'update':
    case 'save':
      return <Edit3 size={16} />;
    case 'delete':
      return <Trash2 size={16} />;
    case 'publish':
      return <Check size={16} />;
    case 'draft':
      return <Edit3 size={16} />;
    default:
      return null;
  }
};

const getTypeIcon = (type: ToastType) => {
  switch (type) {
    case 'success':
      return <Check size={16} />;
    case 'error':
      return <X size={16} />;
    case 'warning':
      return <AlertCircle size={16} />;
    case 'info':
      return <Info size={16} />;
    default:
      return <Info size={16} />;
  }
};

const ToastItem: React.FC<{ toast: Toast; onClose: (id: string) => void }> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(toast.id);
    }, 300); // Match the CSS animation duration
  }, [onClose, toast.id]);

  useEffect(() => {
    if (toast.autoClose !== false && toast.duration !== 0) {
      const duration = toast.duration || 4000;
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.autoClose, handleClose]);

  return (
    <div 
      className={`nb-toast nb-toast--${toast.type} ${isVisible ? 'nb-toast--visible' : ''} ${isLeaving ? 'nb-toast--leaving' : ''}`}
    >
      <div className="nb-toast__content">
        <div className="nb-toast__icon">
          {toast.action ? getActionIcon(toast.action) : getTypeIcon(toast.type)}
        </div>
        <div className="nb-toast__text">
          <div className="nb-toast__title">{toast.title}</div>
          {toast.message && <div className="nb-toast__message">{toast.message}</div>}
        </div>
        <button 
          className="nb-toast__close"
          onClick={handleClose}
          aria-label="Close notification"
        >
          <X size={14} />
        </button>
      </div>
      <div className="nb-toast__progress"></div>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts, hideToast } = useToast();

  return (
    <div className="nb-toast-container">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={hideToast} />
      ))}
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const newToast: Toast = {
      id: generateId(),
      duration: 4000,
      autoClose: true,
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((title: string, message?: string, action?: ToastAction) => {
    showToast({ type: 'success', title, message, action });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, action?: ToastAction) => {
    showToast({ type: 'error', title, message, action, duration: 6000 }); // Longer duration for errors
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, action?: ToastAction) => {
    showToast({ type: 'warning', title, message, action, duration: 5000 });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, action?: ToastAction) => {
    showToast({ type: 'info', title, message, action });
  }, [showToast]);

  const contextValue: ToastContextType = {
    toasts,
    showToast,
    hideToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};
